import React from "react";
import {
  AlertCircle,
  Bot,
  Building,
  Building2,
  Calendar,
  Camera,
  Check,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  CircleUser,
  ClipboardCheck,
  Clock,
  CloudAlert,
  FileText,
  Filter,
  GraduationCap,
  HandCoins,
  Handshake,
  Inbox,
  Info,
  LayoutGrid,
  Lock,
  MapPin,
  Menu,
  MessageCircle,
  Phone,
  PlayCircle,
  QrCode,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Store,
  TicketPercent,
  User,
  UserCheck,
  UserSearch,
  Users,
  X,
  XCircle,
  type LucideIcon,
} from "lucide-react";

/**
 * Maps the RN app's icon names (MaterialCommunityIcons / Ionicons / etc.)
 * onto lucide-react components. Keyed by icon name only, since the names are
 * semantically consistent across the RN icon sets used in the admin screens.
 */
const ICON_MAP: Record<string, LucideIcon> = {
  // MaterialCommunityIcons
  "check-circle": CheckCircle2,
  "check-circle-outline": CheckCircle,
  "alert-circle": AlertCircle,
  "alert-circle-outline": AlertCircle,
  "account-circle": CircleUser,
  "chat-outline": MessageCircle,
  "shield-check": ShieldCheck,
  "shield-alert-outline": ShieldAlert,
  "clipboard-check": ClipboardCheck,
  "clipboard-check-outline": ClipboardCheck,
  domain: Building2,
  "domain-off": Building,
  school: GraduationCap,
  "account-search-outline": UserSearch,
  "account-group": Users,
  "account-group-outline": Users,
  "account-multiple": Users,
  "account-check-outline": UserCheck,
  account: User,
  "cloud-alert-outline": CloudAlert,
  "chevron-right": ChevronRight,
  "chevron-left": ChevronLeft,
  "chevron-down": ChevronDown,
  "clock-outline": Clock,
  "view-grid-outline": LayoutGrid,
  calendar: Calendar,
  "file-document-outline": FileText,
  "file-pdf-box": FileText,
  "hand-coin-outline": HandCoins,
  "handshake-outline": Handshake,
  "lock-outline": Lock,
  lock: Lock,
  sync: RefreshCw,
  check: Check,
  close: X,
  "close-circle": XCircle,
  "close-circle-outline": XCircle,
  "information-outline": Info,
  instagram: Camera,
  "play-circle": PlayCircle,
  robot: Bot,
  "google-my-business": Store,
  "inbox-outline": Inbox,
  refresh: RefreshCw,
  "qrcode-scan": QrCode,
  "qr-code": QrCode,
  "ticket-outline": TicketPercent,
  "ticket-percent-outline": TicketPercent,
  "storefront-outline": Store,
  menu: Menu,
  "menu-outline": Menu,
  // Ionicons / Feather / FontAwesome / MaterialIcons / AntDesign
  search: Search,
  "search-outline": Search,
  "calendar-outline": Calendar,
  "call-outline": Phone,
  "location-outline": MapPin,
  filter: Filter,
  "file-text": FileText,
};

interface IconProps {
  iconType?: string;
  name?: string;
  size?: number;
  color?: string;
  className?: string;
  strokeWidth?: number;
}

/**
 * Web replacement for the RN `Icon` component. Accepts the same
 * `iconType`/`name` props so ported screens can pass their existing values.
 */
const Icon: React.FC<IconProps> = ({
  iconType,
  name,
  size = 20,
  color = "#222",
  className,
  strokeWidth = 2,
}) => {
  const Component = (name && ICON_MAP[name]) || Circle;
  return (
    <Component
      size={size}
      color={color}
      strokeWidth={strokeWidth}
      className={className}
      aria-hidden="true"
    />
  );
};

export default Icon;
